import { useEffect, useState, useCallback } from "react";
import apiClient from "@/lib/apiClient";
import { toast } from "sonner";
import { MessageSquare, Lock, Send, CornerDownRight, Clock, GitCommitHorizontal, Loader2 } from "lucide-react";

const STATUS_LABELS = { new: "New", in_progress: "In progress", resolved: "Resolved", wont_fix: "Won't fix" };

function describe(a) {
  switch (a.action) {
    case "created": return "Submitted this feedback";
    case "status_changed": return `Status → ${STATUS_LABELS[a.to_val] || a.to_val}`;
    case "priority_changed": return `Priority → ${a.to_val}`;
    case "tag_added": return `Added tag “${a.to_val}”`;
    case "tag_removed": return `Removed tag “${a.from_val}”`;
    case "archived": return "Archived";
    case "restored": return "Restored from archive";
    case "deleted": return "Deleted";
    case "jira_linked": return `Linked to Jira · ${a.to_val}`;
    default: return a.action;
  }
}

function timeStr(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export const FeedbackTimeline = ({ feedbackId, canEdit, submitterEmail, notifyOptin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("internal"); // internal | public
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const hasEmail = !!(submitterEmail && String(submitterEmail).includes("@"));
  const willEmail = hasEmail && !!notifyOptin;
  // A tester can only ever see a public reply if they left an email — the status
  // page link is delivered by email. With no contact info, replying is a dead end.
  const canReplyTester = hasEmail;

  useEffect(() => { if (!canReplyTester && mode === "public") setMode("internal"); }, [canReplyTester, mode]);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get(`/feedback/${feedbackId}/activity`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [feedbackId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const url = mode === "public" ? `/feedback/${feedbackId}/reply` : `/feedback/${feedbackId}/comment`;
      const payload = mode === "public" ? { body: text, notify: true } : { body: text };
      const { data } = await apiClient.post(url, payload);
      setItems((prev) => [...prev, data]);
      setBody("");
      toast.success(mode === "public" ? (willEmail ? "Reply sent — tester emailed" : "Reply posted to status page") : "Note added");
    } catch (e) {
      toast.error("Could not post. Try again.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="feedback-timeline">
      <p className="label-overline mb-2 flex items-center gap-1.5"><GitCommitHorizontal className="h-3.5 w-3.5" /> Activity &amp; replies</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-ink/50 font-600 py-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink/40 font-600 py-1">No activity yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((a) => {
            if (a.kind === "comment") {
              return (
                <li key={a.id} className="bg-sunny/25 border-2 border-ink rounded-2xl p-3" data-testid={`timeline-comment-${a.id}`}>
                  <div className="flex items-center gap-1.5 text-xs font-800"><Lock className="h-3 w-3" /> {a.actor_name} · <span className="text-ink/50 font-700">internal note</span><span className="ml-auto text-ink/45 font-600 flex items-center gap-1"><Clock className="h-3 w-3" />{timeStr(a.created_at)}</span></div>
                  <p className="text-sm font-600 whitespace-pre-wrap mt-1.5">{a.body}</p>
                </li>
              );
            }
            if (a.kind === "reply") {
              const tester = a.actor_type === "tester";
              return (
                <li key={a.id} className={`border-2 border-ink rounded-2xl p-3 ${tester ? "bg-sand" : "bg-teal/25"}`} data-testid={`timeline-reply-${a.id}`}>
                  <div className="flex items-center gap-1.5 text-xs font-800"><CornerDownRight className="h-3 w-3" /> {a.actor_name} · <span className="text-ink/50 font-700">{tester ? "tester reply" : "public reply"}</span><span className="ml-auto text-ink/45 font-600 flex items-center gap-1"><Clock className="h-3 w-3" />{timeStr(a.created_at)}</span></div>
                  <p className="text-sm font-600 whitespace-pre-wrap mt-1.5">{a.body}</p>
                </li>
              );
            }
            return (
              <li key={a.id} className="flex items-center gap-2 text-xs font-700 text-ink/70 pl-1" data-testid={`timeline-event-${a.id}`}>
                <span className="h-2 w-2 rounded-full bg-ink/30 shrink-0" />
                <span className="font-800 text-ink">{a.actor_name}</span>
                <span>{describe(a)}</span>
                <span className="ml-auto text-ink/40 font-600 shrink-0">{timeStr(a.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <div className="mt-3 border-2 border-ink rounded-2xl p-2.5 bg-white">
          <div className="flex gap-1.5 mb-2">
            <button onClick={() => setMode("internal")} data-testid="timeline-mode-internal"
              className={`text-xs font-800 px-3 py-1.5 rounded-full border-2 border-ink flex items-center gap-1.5 transition-colors ${mode === "internal" ? "bg-sunny" : "bg-white text-ink/60"}`}>
              <Lock className="h-3 w-3" /> Internal note
            </button>
            <button onClick={() => canReplyTester && setMode("public")} data-testid="timeline-mode-public"
              disabled={!canReplyTester}
              title={canReplyTester ? "" : "This tester left no contact info, so they can't be notified or view replies."}
              className={`text-xs font-800 px-3 py-1.5 rounded-full border-2 border-ink flex items-center gap-1.5 transition-colors ${mode === "public" ? "bg-teal" : "bg-white text-ink/60"} ${!canReplyTester ? "opacity-40 cursor-not-allowed" : ""}`}>
              <MessageSquare className="h-3 w-3" /> Reply to tester
            </button>
          </div>
          {!canReplyTester && (
            <p data-testid="reply-disabled-note" className="text-[11px] font-700 text-ink/50 mb-2 flex items-center gap-1">
              ⚠ No contact info on this report — you can only add an internal note.
            </p>
          )}
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} data-testid="timeline-composer-input"
            placeholder={mode === "public" ? "Reply to the tester — they'll be emailed if they opted in…" : "Add an internal note for your team…"}
            className="w-full text-sm font-600 border-2 border-ink/25 focus:border-ink rounded-xl p-2.5 outline-none resize-none" />
          {mode === "public" && (
            <div data-testid="reply-notify-note"
              className={`text-[11px] font-700 rounded-lg px-2.5 py-1.5 mt-1.5 border-2 ${willEmail ? "bg-grass/20 border-grass/60 text-ink" : "bg-sunny/50 border-ink/20 text-ink"}`}>
              {willEmail
                ? <>✓ <b>{submitterEmail}</b> will be emailed when you reply.</>
                : hasEmail
                  ? <><b>{submitterEmail}</b> is on file, but this tester didn’t opt in to updates — they <b>won’t be emailed</b>. Your reply only shows on their private status link.</>
                  : <>⚠ This tester didn’t leave contact info, so they <b>can’t be notified</b>. Your reply only appears on the private status link.</>}
            </div>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] font-700 text-ink/45">{mode === "public" ? "Shared with the tester." : "Only your team can see this."}</span>
            <button onClick={submit} disabled={sending || !body.trim()} data-testid="timeline-composer-send"
              className="nb-btn bg-orange text-white text-sm px-4 py-2 disabled:opacity-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> {mode === "public" ? (willEmail ? "Send \u0026 email" : "Post reply") : "Add note"}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
